// Activity time-conflict detection (client-side).
//
// The deterministic server scheduler sequences a day's stops (arrive =
// prev.depart + travel), so it can NEVER hold two stops at the same or
// overlapping time — it will re-time one of them, and which one used to be an
// arbitrary tie-break (the stops' random-UUID order). Rather than let the app
// silently pick, we detect the clash before saving — the trip screen already
// holds every stop — and let the user choose the order (before/after).
//
// Two clash kinds, surfaced separately because they read differently to a user:
//   - sameStart: identical start time — the order is genuinely undefined.
//   - overlap:   partial or nested ("during") — one runs inside/across another.
// An activity is reported in at most one bucket (sameStart wins).

export interface ConflictActivity {
  id: string;
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  title?: string;    // for the picker copy
}

export interface ConflictDraft {
  startTime: string;    // "HH:MM"
  endTime?: string;     // "HH:MM" — defaults to a point at startTime
  excludeId?: string;   // when editing, the activity being edited ignores itself
}

export interface ConflictResult {
  sameStart: ConflictActivity[];
  overlap: ConflictActivity[];
}

/** "HH:MM" → minutes since midnight, or null when unparseable. */
const toMinutes = (hhmm: string | undefined): number | null => {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
};

/** Half-open interval overlap: [aStart,aEnd) ∩ [bStart,bEnd) ≠ ∅. Back-to-back
 * (aEnd === bStart) is NOT an overlap. A zero-length interval is treated as the
 * point [t, t] so a stop landing exactly inside another still counts. */
const intervalsOverlap = (aStart: number, aEnd: number, bStart: number, bEnd: number): boolean => {
  const aHi = Math.max(aEnd, aStart);
  const bHi = Math.max(bEnd, bStart);
  if (aStart === aHi) return bStart <= aStart && aStart < bHi; // a is a point
  if (bStart === bHi) return aStart <= bStart && bStart < aHi; // b is a point
  return aStart < bHi && bStart < aHi;
};

/**
 * Find stops on the same day that clash with a draft's time. `sameDayActivities`
 * must already be filtered to the draft's day.
 */
export const detectConflicts = (
  draft: ConflictDraft,
  sameDayActivities: ConflictActivity[],
): ConflictResult => {
  const start = toMinutes(draft.startTime);
  const result: ConflictResult = { sameStart: [], overlap: [] };
  if (start === null) return result; // an untimed draft can't clash on time

  const end = toMinutes(draft.endTime) ?? start;

  for (const act of sameDayActivities) {
    if (draft.excludeId && act.id === draft.excludeId) continue;
    const aStart = toMinutes(act.startTime);
    if (aStart === null) continue;
    const aEnd = toMinutes(act.endTime) ?? aStart;

    if (aStart === start) {
      result.sameStart.push(act);
    } else if (intervalsOverlap(start, end, aStart, aEnd)) {
      result.overlap.push(act);
    }
  }
  return result;
};

/** True when a draft clashes with anything — the caller must resolve first. */
export const hasConflict = (result: ConflictResult): boolean =>
  result.sameStart.length > 0 || result.overlap.length > 0;
